// Helpers for dragging photos between slots in the interactive editor embed
// (view=preview only — the handlers using these are gated in Photo.jsx the
// same way editable() gates its markers, so the print/PDF DOM never carries
// draggable attributes or drag handlers).

// Custom MIME type identifying our drag payload. During dragover only
// dataTransfer.types is readable (the data itself is only available on
// drop), so drop targets — including ones in the other facing page's
// same-origin iframe — recognize the drag by this type alone.
export const PHOTO_DRAG_MIME = "application/x-babynotebook-photo";

export function serializePhotoDragData(questionId) {
  return JSON.stringify({ questionId });
}

// True when the in-flight drag carries our photo payload. `types` is a
// frozen array in modern browsers but a DOMStringList in older ones, so
// avoid Array methods on it directly.
export function hasPhotoDragType(dataTransfer) {
  const types = dataTransfer?.types;
  if (!types) return false;
  for (let i = 0; i < types.length; i += 1) {
    if (types[i] === PHOTO_DRAG_MIME) return true;
  }
  return false;
}

// Parses the payload read on drop. Returns the source question id, or null
// when the payload is missing/malformed.
export function parsePhotoDragQuestionId(raw) {
  if (!raw) return null;
  try {
    const data = JSON.parse(raw);
    return typeof data?.questionId === "string" && data.questionId
      ? data.questionId
      : null;
  } catch (e) {
    return null;
  }
}

// Custom drag ghost. The page renders in a fixed 1000px viewport that the
// HOST scales down with a CSS transform, but browsers capture the default
// drag image at the element's internal (unscaled) size — dragging a photo
// ghosts it at near-full-page size. Draw a small thumbnail on a canvas and
// use that instead. Failures fall back silently to the default ghost
// (cosmetic only).
export const DRAG_GHOST_SIZE = 120;

export function setScaledDragImage(event) {
  try {
    const dataTransfer = event?.dataTransfer;
    if (!dataTransfer || typeof dataTransfer.setDragImage !== "function") {
      return;
    }
    const target = event.currentTarget;
    const img =
      target?.tagName === "IMG" ? target : target?.querySelector?.("img");
    if (!img) return;
    const width = img.naturalWidth || img.width;
    const height = img.naturalHeight || img.height;
    if (!width || !height) return;
    const scale = Math.min(DRAG_GHOST_SIZE / width, DRAG_GHOST_SIZE / height, 1);
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(width * scale));
    canvas.height = Math.max(1, Math.round(height * scale));
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    // setDragImage sources must be connected to the document; park the
    // canvas offscreen for the capture and discard it right after.
    canvas.style.position = "fixed";
    canvas.style.top = "-1000px";
    canvas.style.left = "-1000px";
    document.body.appendChild(canvas);
    dataTransfer.setDragImage(canvas, canvas.width / 2, canvas.height / 2);
    setTimeout(() => canvas.remove(), 0);
  } catch (e) {
    // e.g. a tainted canvas — keep the default ghost.
  }
}

// Post-drag click suppression. Some browsers fire a click on the drag
// source after the drag ends; without this guard that click would reopen
// the host's image picker. dragend marks the moment, and the click handler
// consumes the mark — suppressing only a click that follows within the
// window. The `now` parameters exist for tests.
const POST_DRAG_CLICK_SUPPRESS_MS = 500;
let dragEndedAt = 0;

export function markDragJustEnded(now = Date.now()) {
  dragEndedAt = now;
}

export function consumePostDragClick(now = Date.now()) {
  if (!dragEndedAt) return false;
  const suppress = now - dragEndedAt < POST_DRAG_CLICK_SUPPRESS_MS;
  dragEndedAt = 0;
  return suppress;
}
